import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { processPayment, getUserSubscription } from '../utils/firebaseAuth';

const API_BASE = 'https://us-central1-losujemy.cloudfunctions.net/api';

export default function PaymentSuccess({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function finalizePayment() {
      const urlStatus = searchParams.get('status');
      const urlMethod = searchParams.get('method');
      const urlAmount = searchParams.get('amount');
      const urlPlan = searchParams.get('plan');
      const urlSessionId = searchParams.get('sessionId');

      if (urlStatus === 'success' && urlMethod === 'paypal') {
        setStatus('success');
        setDetails({ method: 'paypal', amount: urlAmount, plan: urlPlan });
        return;
      }

      const pendingRaw = sessionStorage.getItem('p24_pending');
      const pending = pendingRaw ? JSON.parse(pendingRaw) : null;
      const sessionId = urlSessionId || pending?.sessionId;

      if (!sessionId || !user?.uid) {
        if (urlStatus === 'success') {
          setStatus('success');
          setDetails({ method: urlMethod || 'unknown', amount: urlAmount, plan: urlPlan });
        } else {
          setStatus('unknown');
        }
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/p24/finalize-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId, userId: user.uid }),
        });
        const data = await res.json();

        if (!cancelled && data?.success) {
          sessionStorage.removeItem('p24_pending');
          const sub = await getUserSubscription(user.uid);
          if (sub?.subscription_status !== 'active') {
            await processPayment(
              user.uid,
              Number(pending?.amount || data.amount || 9.99),
              pending?.method || 'blik',
              pending?.plan || 'monthly'
            );
          }
          setStatus('success');
          setDetails({
            method: pending?.method || 'blik',
            amount: pending?.amount || data.amount,
            plan: pending?.plan || 'monthly',
          });
          return;
        }

        if (!cancelled) {
          setError(data?.error || 'Nie udało się potwierdzić płatności');
          setStatus('error');
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Błąd weryfikacji płatności');
          setStatus('error');
        }
      }
    }

    finalizePayment();
    return () => { cancelled = true; };
  }, [searchParams, user]);

  if (status === 'processing') {
    return (
      <div style={{ maxWidth: 520, margin: '60px auto', padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
        <h1 style={{ fontSize: 22, color: '#333' }}>Potwierdzanie płatności…</h1>
        <p style={{ color: '#666' }}>Proszę czekać, aktywujemy Twoją subskrypcję Premium.</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={{ maxWidth: 520, margin: '60px auto', padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
        <h1 style={{ fontSize: 22, color: '#d32f2f' }}>Problem z potwierdzeniem płatności</h1>
        <p style={{ color: '#666', marginBottom: 20 }}>{error}</p>
        <button
          type="button"
          onClick={() => navigate('/payments')}
          style={{
            background: 'linear-gradient(90deg, #ff9800, #ffb300)',
            color: '#fff', border: 'none', borderRadius: 10, padding: '12px 24px',
            fontWeight: 600, cursor: 'pointer', marginRight: 8,
          }}
        >
          Wróć do płatności
        </button>
        <button
          type="button"
          onClick={() => navigate('/')}
          style={{
            background: '#f5f5f5', color: '#333', border: 'none', borderRadius: 10,
            padding: '12px 24px', fontWeight: 600, cursor: 'pointer',
          }}
        >
          Strona główna
        </button>
      </div>
    );
  }

  const methodLabel = details?.method === 'paypal' ? 'PayPal'
    : details?.method === 'blik' ? 'BLIK'
    : details?.method === 'p24' ? 'Przelewy24'
    : details?.method || 'Płatność';

  return (
    <div style={{ maxWidth: 520, margin: '60px auto', padding: 24, textAlign: 'center' }}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: 32,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '2px solid #4caf50',
      }}>
        <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
        <h1 style={{ color: '#2e7d32', fontSize: 24, marginBottom: 12 }}>
          Płatność zakończona pomyślnie!
        </h1>
        <p style={{ color: '#666', marginBottom: 20 }}>
          Dziękujemy! Twoja subskrypcja Premium została aktywowana.
        </p>
        <div style={{ background: '#f8f9fa', borderRadius: 10, padding: 16, marginBottom: 24, textAlign: 'left' }}>
          <div><strong>Metoda:</strong> {methodLabel}</div>
          {details?.amount && <div><strong>Kwota:</strong> {details.amount} PLN</div>}
        </div>
        <button
          type="button"
          onClick={() => navigate('/generator')}
          style={{
            background: 'linear-gradient(135deg, #4caf50, #45a049)',
            color: '#fff', border: 'none', borderRadius: 10, padding: '12px 28px',
            fontWeight: 600, cursor: 'pointer', marginRight: 8,
          }}
        >
          Przejdź do generatora
        </button>
        <button
          type="button"
          onClick={() => navigate('/')}
          style={{
            background: '#f5f5f5', color: '#333', border: 'none', borderRadius: 10,
            padding: '12px 28px', fontWeight: 600, cursor: 'pointer',
          }}
        >
          Strona główna
        </button>
      </div>
    </div>
  );
}
